import React from 'react';
import FormModal from './Generic/FormModal';
import SkillForm from './SkillForm';
import { FloppyFill, PlusCircle } from './Generic/Icons';
import useABC from '../Provider/TableContext/ABCContext';
import useSkill from '../Provider/TableContext/SkillContext';

const DefaultFormData = { skill: "", mcb_skill: "", achievements: []};

export default function SkillAssociationModal({title, actionName, nextActionName,  onClose: _onClose,...props}) {
    const [formData, _setFormData] = React.useState(DefaultFormData);
    const { abcs, loading: abcLoading, addRelationToABC, removeRelationFromABC } = useABC();
    const { skills, updateSkill, loading: skillsLoading } = useSkill();
    
    const loading = React.useMemo(() => abcLoading || skillsLoading, [abcLoading, skillsLoading]);

    const currentSkillName = React.useMemo(() => (
        Array.isArray(skills) && skills.find(skill => skill.mcb_skillid === formData.skill)?.mcb_skill
    ) || "", [formData.skill, skills]);

    const onClose = React.useCallback(() => {
        _setFormData(DefaultFormData)
        _onClose?.(); 
    }, [_onClose]);

    const skillsAchievements = React.useMemo(() => {
        const result = {};
        if (Array.isArray(abcs)) {
            abcs.forEach(({mcb_ABCSkill, mcb_abcid} = {}) => {
                if (mcb_abcid && Array.isArray(mcb_ABCSkill)) {
                    mcb_ABCSkill.forEach(({mcb_skillid}) => {
                        if (Array.isArray(result[mcb_skillid])) {
                            result[mcb_skillid].push(mcb_abcid)
                        } else {
                            result[mcb_skillid] = [mcb_abcid];
                        }
                    })
                }
            })
        }
        return result;
    }, [abcs]);

    const skillAchievements = React.useMemo(() => (
        (skillsAchievements && skillsAchievements[formData?.skill]) || []
    ), [skillsAchievements, formData.skill]);

    const hasChanged = React.useMemo(() => (
        formData.skill && !(
            skillAchievements.length ===  formData.achievements.length
            && skillAchievements.every(oA => formData.achievements.includes(oA))
            && currentSkillName === formData.mcb_skill
        )
    ), [formData, skillAchievements, currentSkillName])

    const onSubmit = React.useCallback(async () => {
        if (!formData?.skill) return false;
        if (!hasChanged) return true;
        const promises = [];

        const mcb_skillid = formData.skill;
        if (currentSkillName !== formData.skill) {
            const mcb_skill = formData.mcb_skill;
            promises.push(updateSkill({ mcb_skillid, mcb_skill }));
        }
        
        const oldAchievements = Array.from(skillAchievements);
        formData.achievements.forEach(associated => {
            const oldIndex = oldAchievements.findIndex(oA => oA === associated);

            if (oldIndex < 0) {
                // associating new relations which are not present
                promises.push(addRelationToABC(associated, "mcb_ABCSkill", mcb_skillid));
                return false;
            } else {
                // removing all the common achievements from the oldAchievements
                oldAchievements.splice(oldIndex, 1);
                return true;
            }
        });

        // after removing all common ones oldAchievements array should only have a 
        oldAchievements.forEach(disassociated => {
            promises.push(removeRelationFromABC(disassociated, "mcb_ABCSkill", mcb_skillid));
        })

        try {
            await Promise.all(promises);
        } catch (error) {
            console.error(error);
        }
        return true;
    }, [formData, updateSkill, currentSkillName, skillAchievements, addRelationToABC, removeRelationFromABC, hasChanged]);
    
    const setFormData = React.useCallback((arg) => {
        _setFormData((oldData) => {
            let newData = typeof arg === "function"? arg(oldData) : arg;
            if (oldData.skill !== newData.skill) {
                newData.achievements = skillsAchievements[newData.skill] || [];
                const skill = skills.find(skill => skill.mcb_skillid === newData.skill);
                newData.mcb_skill = skill?.mcb_skill;
            }
            return newData;
        })
    }, [skillsAchievements, skills]);

    return (
        <>
            <FormModal
                title={(title || "Associate Skills to Achievements")}
                triggerName={actionName || <><PlusCircle className="me-2"/> Associate Skills</>}
                actionName={(actionName || <><FloppyFill className="me-2"/> Associate Skills</>)}
                classNames={{
                    modalTitleClassName: "btn-primary",
                    modalDialogClassName: "modal-dialog-centered", 
                    modalTriggerClassName: "btn-outline-dark border-0 fs-5 p-0 shadow-none text-decoration-underline",
                    closeActionClassName: "btn icon-button",
                }}
                BodyComponent={SkillForm}
                onClose={onClose}
                formData={formData}
                setFormData={setFormData}
                onSubmit={onSubmit}
                loading={loading}
                quitConfirmation={hasChanged}
                disableSubmit={!formData?.achievements?.length || !formData?.skill}
                {...props}
            />
        </>
    );
}